import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchStockData, getStockInfo } from "@/utils/api";
import { InvestmentCalculator } from "@/components/InvestmentCalculator";
import { StockChart } from "@/components/StockChart";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useEffect } from "react";
import { toast } from "sonner";

const Stock = () => {
  const { symbol } = useParams<{ symbol: string }>();
  
  const { data: stockData, isLoading: isLoadingHistory, error: historyError } = useQuery({
    queryKey: ["stockHistory", symbol],
    queryFn: () => fetchStockData(symbol?.toUpperCase() || ""),
    enabled: !!symbol,
    retry: 1,
    meta: {
      onError: (error: Error) => {
        console.error("Error fetching stock history:", error);
        toast.error("Unable to load stock history data. Please try again later.");
      }
    }
  });

  const { data: stockInfo, isLoading: isLoadingInfo, error: infoError } = useQuery({
    queryKey: ["stockInfo", symbol],
    queryFn: () => getStockInfo(symbol?.toUpperCase() || ""),
    enabled: !!symbol,
    retry: 1,
    meta: {
      onError: (error: Error) => {
        console.error("Error fetching stock info:", error);
        toast.error("Unable to load stock information. Please try again later.");
      }
    }
  });

  // Update metadata when stock info is loaded
  useEffect(() => {
    if (stockInfo) {
      // Update title
      document.title = `${stockInfo.name} (${symbol?.toUpperCase()}) Stock Calculator - Invested Calculator`;
      
      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute(
          'content',
          `Calculate historical investment returns for ${stockInfo.name} (${symbol?.toUpperCase()}) stock. Free calculator with dividend reinvestment analysis and investment performance tracking.`
        );
      }

      // Update Open Graph title and description
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) {
        ogTitle.setAttribute(
          'content',
          `${stockInfo.name} (${symbol?.toUpperCase()}) Investment Calculator`
        );
      }

      const ogDescription = document.querySelector('meta[property="og:description"]');
      if (ogDescription) {
        ogDescription.setAttribute(
          'content',
          `Analyze your potential returns if you had invested in ${stockInfo.name} stock. Calculate historical performance with our free investment calculator.`
        );
      }

      // Update Twitter meta tags
      const twitterTitle = document.querySelector('meta[name="twitter:title"]');
      if (twitterTitle) {
        twitterTitle.setAttribute(
          'content',
          `${stockInfo.name} (${symbol?.toUpperCase()}) Stock Return Calculator`
        );
      }

      const twitterDescription = document.querySelector('meta[name="twitter:description"]');
      if (twitterDescription) {
        twitterDescription.setAttribute(
          'content',
          `Find out how much you could have made investing in ${stockInfo.name} stock. Free investment return calculator with dividend analysis.`
        );
      }
    }
  }, [stockInfo, symbol]);

  if (isLoadingHistory || isLoadingInfo) {
    return (
      <div className="container mx-auto px-4 py-8 space-y-8">
        <Skeleton className="h-12 w-2/3" />
        <Skeleton className="h-[400px]" />
        <Skeleton className="h-[200px]" />
      </div>
    );
  }

  if (historyError || infoError || !stockData?.values || !stockInfo) {
    return (
      <div className="container mx-auto px-4 py-8 flex flex-col items-center justify-center">
        <h1 className="text-3xl font-bold mb-4">Stock Not Found</h1>
        <p className="text-muted-foreground mb-6 text-center max-w-lg">
          We couldn't find data for the stock symbol "{symbol?.toUpperCase()}". 
          This could be because:
        </p>
        <ul className="list-disc text-muted-foreground mb-8 max-w-lg">
          <li>The stock symbol is incorrect</li>
          <li>The stock is not currently supported</li>
          <li>There might be a temporary issue with our data provider</li>
        </ul>
        <a 
          href="/"
          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
        >
          Return to homepage and try another stock
        </a>
      </div>
    );
  }

  // Generate FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": `What is dollar-cost averaging for ${stockInfo.name} stock?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `Dollar-cost averaging is an investment strategy where you invest a fixed amount in ${stockInfo.name} stock regularly, regardless of the share price. This helps reduce the impact of market volatility on your investment.`
        }
      },
      {
        "@type": "Question",
        "name": `How do stock splits affect my ${stockInfo.name} investment calculation?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `Stock splits are automatically factored into our calculator. When ${stockInfo.name} performs a stock split, the historical prices are adjusted accordingly to show accurate returns on investment.`
        }
      },
      {
        "@type": "Question",
        "name": `Are dividends included in the ${stockInfo.name} investment calculator?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `Yes, our calculator includes dividend reinvestment in the total return calculation for ${stockInfo.name} stock, assuming all dividends are reinvested to purchase additional shares.`
        }
      },
      {
        "@type": "Question",
        "name": `How accurate is the historical data for ${stockInfo.name}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `We use reliable financial data providers to ensure accurate historical pricing data for ${stockInfo.name}, including adjustments for splits and dividends. However, past performance doesn't guarantee future results.`
        }
      }
    ]
  };

  // Generate Investment Calculator Schema
  const calculatorSchema = {
    "@context": "https://schema.org",
    "@type": "FinancialProduct",
    "name": `${stockInfo.name} Investment Calculator`,
    "description": `Calculate potential returns from investing in ${stockInfo.name} (${symbol}) stock over different time periods.`,
    "category": "Investment Calculator",
    "featureList": [
      "Historical price analysis",
      "Dividend reinvestment calculations",
      "Dollar-cost averaging support",
      "Split-adjusted returns"
    ]
  };

  return (
    <div className="container mx-auto px-4 py-8 space-y-8">
      {/* Add Schema.org structured data */}
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(calculatorSchema)}
      </script>

      <h1 className="text-4xl font-bold mb-8">{stockInfo.name} ({symbol?.toUpperCase()}) Stock Calculator</h1>
      
      <InvestmentCalculator stockData={stockData.values} symbol={symbol || ""} />
      <StockChart data={stockData.values} />

      {/* FAQ Section */}
      <div className="mt-12 max-w-3xl mx-auto">
        <h2 className="text-2xl font-semibold mb-6">Frequently Asked Questions</h2>
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="item-1">
            <AccordionTrigger>What is dollar-cost averaging for {stockInfo.name} stock?</AccordionTrigger>
            <AccordionContent>
              Dollar-cost averaging is an investment strategy where you invest a fixed amount in {stockInfo.name} stock 
              regularly, regardless of the share price. This helps reduce the impact of market volatility on your investment.
              By investing the same amount at regular intervals, you buy more shares when prices are low and fewer shares 
              when prices are high, potentially lowering your average cost per share over time.
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="item-2">
            <AccordionTrigger>How do stock splits affect my {stockInfo.name} investment calculation?</AccordionTrigger>
            <AccordionContent>
              Stock splits are automatically factored into our calculator. When {stockInfo.name} performs a stock split, 
              the historical prices are adjusted accordingly to show accurate returns on investment. For example, if you 
              owned 100 shares before a 2-for-1 split, you would have 200 shares after the split, but the total value 
              of your investment remains the same.
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="item-3">
            <AccordionTrigger>Are dividends included in the {stockInfo.name} investment calculator?</AccordionTrigger>
            <AccordionContent>
              Yes, our calculator includes dividend reinvestment in the total return calculation for {stockInfo.name} stock, 
              assuming all dividends are reinvested to purchase additional shares. This provides a more accurate picture of 
              your potential returns, as dividend reinvestment can significantly impact long-term investment growth through 
              compound returns.
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="item-4">
            <AccordionTrigger>How accurate is the historical data for {stockInfo.name}?</AccordionTrigger>
            <AccordionContent>
              We use reliable financial data providers to ensure accurate historical pricing data for {stockInfo.name}, 
              including adjustments for splits and dividends. However, it's important to note that past performance 
              doesn't guarantee future results. The calculator is meant to be an educational tool to understand historical 
              performance and should not be the sole factor in making investment decisions.
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
};

export default Stock;
